/**
 * Reactive reads.
 *
 * `useLiveQuery` re-runs its query whenever the underlying Dexie tables change,
 * so a sync pull landing in the background repaints the open screen with no
 * refetch, no cache invalidation and no stale-time tuning. That is the payoff of
 * making IndexedDB the read source instead of a cache in front of the network.
 *
 * Joins happen in memory. At 67 moulds and ~200 master rows the entire catalog
 * is a rounding error, and an in-memory join is both faster and simpler than
 * maintaining compound indexes for every filter combination the UI offers.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import type { MasterBase, Mould } from '@mouldtrack/shared';
import { db, type Local } from './schema.js';

/** The eleven catalog master tables, in the order they appear in navigation. */
export const CATALOG_MASTER_TABLES = [
  'companies',
  'projects',
  'designers',
  'toolTypes',
  'runnerTypes',
  'gateTypes',
  'ejectionTypes',
  'materials',
  'materialGrades',
  'mechanismTypes',
  'nameplateTemplates',
] as const;
export type CatalogMasterTable = (typeof CATALOG_MASTER_TABLES)[number];

/**
 * The places a mould can be, as opposed to what a mould is made of.
 *
 * Separate from the catalog masters because they are a different kind of thing:
 * the eleven catalog tables describe a tool, these three describe the site it
 * sits on and the points where it gets scanned. They are replicated by the same
 * sync engine and edited by the same screens, but they carry no `isActive` flag
 * and they reference each other, so the type stays distinct.
 */
export const PLACE_TABLES = ['locations', 'stores', 'checkpoints'] as const;
export type PlaceTable = (typeof PLACE_TABLES)[number];

/** Any table the generic master screens can manage. */
export type ManagedTable = CatalogMasterTable | PlaceTable;

/**
 * True when a mould has no name of its own.
 *
 * Some rows in the client's sheet carry only a code in the "MOLD CODE AND NAME"
 * cell. The record still needs a name - the column is NOT NULL - so the code is
 * used, but showing it twice side by side ("9526177 9526177") reads as a
 * rendering bug rather than as an absent name.
 */
export function hasOwnName(mould: { assetCode?: string | null; name?: string | null }): boolean {
  const code = (mould.assetCode ?? '').trim().toUpperCase();
  const name = (mould.name ?? '').trim().toUpperCase();
  return name.length > 0 && name !== code;
}

/** A mould with its master relations resolved to display names. */
export interface MouldRow extends Local<Mould> {
  projectName: string | null;
  /** The project's code, as the client's sheets write it before the name. */
  projectCode: string | null;
  ownerCompanyName: string | null;
  customerCompanyName: string | null;
  manufactureCompanyName: string | null;
  designerName: string | null;
  toolTypeName: string | null;
  runnerTypeName: string | null;
  gateTypeName: string | null;
  ejectionTypeName: string | null;
  coreCavityMaterialName: string | null;
  moldBaseMaterialName: string | null;
  materialGradeName: string | null;
  mechanismTypeName: string | null;
  nameplateTemplateName: string | null;
  /** The chosen plate design's code - what the server's nameplate endpoint takes. */
  nameplateTemplateCode: string | null;
}

export interface MouldFilters {
  search?: string;
  projectId?: string;
  ownerCompanyId?: string;
  toolTypeId?: string;
  manufactureYear?: number;
  includeInactive?: boolean;
  /**
   * Keep only the moulds this says are missing something.
   *
   * Every other filter here asks "which value does this field hold"; this one
   * asks "does it hold one at all". It arrives as a predicate rather than a
   * rule name so this layer need not know what a data-quality rule is - the
   * rules live with the screens that explain them.
   *
   * It exists so a number on the dashboard can be a link: twenty unassigned
   * moulds is only useful if clicking it lands on exactly those twenty.
   */
  gapTest?: (m: MouldRow) => boolean;
  /** Changes when `gapTest` means something different, so the query re-runs. */
  gapKey?: string;
}

export type MouldSortKey = 'assetCode' | 'name' | 'updatedAt' | 'manufactureYear';

function nameOf(map: Map<string, { name: string }>, id: string | null | undefined): string | null {
  return id ? (map.get(id)?.name ?? null) : null;
}

async function loadMasterMaps() {
  const [
    companies,
    projects,
    designers,
    toolTypes,
    runnerTypes,
    gateTypes,
    ejectionTypes,
    materials,
    materialGrades,
    mechanismTypes,
    nameplateTemplates,
  ] = await Promise.all([
    db.companies.toArray(),
    db.projects.toArray(),
    db.designers.toArray(),
    db.toolTypes.toArray(),
    db.runnerTypes.toArray(),
    db.gateTypes.toArray(),
    db.ejectionTypes.toArray(),
    db.materials.toArray(),
    db.materialGrades.toArray(),
    db.mechanismTypes.toArray(),
    db.nameplateTemplates.toArray(),
  ]);

  const toMap = <T extends { id: string }>(rows: T[]) => new Map(rows.map((r) => [r.id, r]));
  return {
    companies: toMap(companies),
    projects: toMap(projects),
    designers: toMap(designers),
    toolTypes: toMap(toolTypes),
    runnerTypes: toMap(runnerTypes),
    gateTypes: toMap(gateTypes),
    ejectionTypes: toMap(ejectionTypes),
    materials: toMap(materials),
    materialGrades: toMap(materialGrades),
    mechanismTypes: toMap(mechanismTypes),
    nameplateTemplates: toMap(nameplateTemplates),
  };
}

function hydrate(mould: Local<Mould>, maps: Awaited<ReturnType<typeof loadMasterMaps>>): MouldRow {
  return {
    ...mould,
    projectName: nameOf(maps.projects, mould.projectId),
    projectCode: (mould.projectId ? (maps.projects.get(mould.projectId) as { code?: string } | undefined)?.code : null) ?? null,
    ownerCompanyName: nameOf(maps.companies, mould.ownerCompanyId),
    customerCompanyName: nameOf(maps.companies, mould.customerCompanyId),
    manufactureCompanyName: nameOf(maps.companies, mould.manufactureCompanyId),
    designerName: nameOf(maps.designers, mould.designerId),
    toolTypeName: nameOf(maps.toolTypes, mould.toolTypeId),
    runnerTypeName: nameOf(maps.runnerTypes, mould.runnerTypeId),
    gateTypeName: nameOf(maps.gateTypes, mould.gateTypeId),
    ejectionTypeName: nameOf(maps.ejectionTypes, mould.ejectionTypeId),
    coreCavityMaterialName: nameOf(maps.materials, mould.coreCavityMaterialId),
    moldBaseMaterialName: nameOf(maps.materials, mould.moldBaseMaterialId),
    materialGradeName: nameOf(maps.materialGrades, mould.materialGradeId),
    mechanismTypeName: nameOf(maps.mechanismTypes, mould.mechanismTypeId),
    nameplateTemplateName: nameOf(maps.nameplateTemplates, mould.nameplateTemplateId),
    nameplateTemplateCode:
      (mould.nameplateTemplateId
        ? (maps.nameplateTemplates.get(mould.nameplateTemplateId) as { code?: string } | undefined)?.code
        : null) ?? null,
  };
}

/** The catalog list. Returns `undefined` while the first query is in flight. */
export function useMoulds(filters: MouldFilters = {}): MouldRow[] | undefined {
  const { search, projectId, ownerCompanyId, toolTypeId, manufactureYear, includeInactive } =
    filters;
  const { gapTest, gapKey } = filters;

  return useLiveQuery(async () => {
    const [moulds, maps] = await Promise.all([db.moulds.toArray(), loadMasterMaps()]);

    const needle = search?.trim().toLowerCase();
    return moulds
      .filter((m) => {
        if (!includeInactive && m.isActive === false) return false;
        if (projectId && m.projectId !== projectId) return false;
        if (ownerCompanyId && m.ownerCompanyId !== ownerCompanyId) return false;
        if (toolTypeId && m.toolTypeId !== toolTypeId) return false;
        if (manufactureYear && m.manufactureYear !== manufactureYear) return false;
        if (needle) {
          const haystack = `${m.assetCode} ${m.name} ${m.moldSize ?? ''} ${m.cavities ?? ''}`;
          if (!haystack.toLowerCase().includes(needle)) return false;
        }
        return true;
      })
      .map((m) => hydrate(m, maps))
      // After hydrating, because a gap is about the resolved name: a mould with
      // an ownerCompanyId pointing at a company that no longer exists has no
      // owner in every sense that matters on screen.
      .filter((m) => (gapTest ? gapTest(m) : true))
      // A mould can legitimately have no code yet: the form invites leaving the
      // Mould ID blank so the server allocates one from the project, so between
      // saving and the next sync the local row has none. Sorting without the
      // guard threw and took the catalog down with it. Unallocated rows sort
      // last, where a row still waiting for an identity belongs.
      .sort((a, b) =>
        (a.assetCode ?? '￿').localeCompare(b.assetCode ?? '￿', undefined, {
          numeric: true,
        }),
      );
  }, [search, projectId, ownerCompanyId, toolTypeId, manufactureYear, includeInactive, gapKey]);
}

/** One mould, by id or by the human `assetCode` the QR encodes. */
export function useMould(idOrCode: string | undefined): MouldRow | null | undefined {
  return useLiveQuery(async () => {
    if (!idOrCode) return null;
    const found =
      (await db.moulds.get(idOrCode)) ??
      (await db.moulds.where('assetCode').equals(idOrCode).first());
    if (!found) return null;
    return hydrate(found, await loadMasterMaps());
  }, [idOrCode]);
}

/**
 * One mould, found the way a scan finds it.
 *
 * Separate from `useMould` because the tolerance is different, and deliberately
 * so. A route parameter is exact - it came from a link this app produced. A
 * scanned code came off a laser-etched plate through a camera, a handheld that
 * types, or a thumb, and only the first of those is reliably exact. The server
 * has always matched case- and space-insensitively; this makes the device agree,
 * so the app cannot say "unknown code" about a mould the server would scan
 * happily. Getting that wrong reads as a broken scanner rather than as a typo.
 *
 * Linear over ~75 rows, which is faster than maintaining an extra index for it.
 */
export function useMouldByScannedCode(code: string | undefined): MouldRow | null | undefined {
  return useLiveQuery(async () => {
    const needle = (code ?? '').trim().toUpperCase();
    if (!needle) return null;
    // A plate's QR carries the mould's id; a label or an older plate its code.
    const byId = await db.moulds.get((code ?? '').trim().toLowerCase()); // a handheld types it in capitals
    if (byId) return hydrate(byId, await loadMasterMaps());
    const exact = await db.moulds.where('assetCode').equals(needle).first();
    const found =
      exact ??
      (await db.moulds.filter((m) => (m.assetCode ?? '').trim().toUpperCase() === needle).first());
    if (!found) return null;
    return hydrate(found, await loadMasterMaps());
  }, [code]);
}

/** Every row of one master table, code-sorted. */
export function useMasterRows<T extends MasterBase>(
  table: ManagedTable,
  includeInactive = true,
): Array<Local<T>> | undefined {
  return useLiveQuery(async () => {
    const rows = (await db.table(table).toArray()) as Array<Local<T>>;
    return rows
      .filter((r) => includeInactive || r.isActive !== false)
      .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '', undefined, { numeric: true }));
  }, [table, includeInactive]);
}

export interface Option {
  value: string;
  label: string;
  hint?: string;
}

/** Active rows of a master table, shaped for a <Select>. */
export function useMasterOptions(table: ManagedTable): Option[] | undefined {
  return useLiveQuery(async () => {
    const rows = (await db.table(table).toArray()) as MasterBase[];
    return rows
      .filter((r) => r.isActive !== false)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((r) => ({ value: r.id, label: r.name, hint: r.code }));
  }, [table]);
}

export interface CatalogStats {
  total: number;
  withPhoto: number;
  pendingSync: number;
  projects: number;
  companies: number;
  byOwner: Array<{ id: string | null; name: string; count: number }>;
  byYear: Array<{ year: number; count: number }>;
}

/** Headline numbers for the overview screen. */
export function useCatalogStats(): CatalogStats | undefined {
  return useLiveQuery(async () => {
    const [moulds, companies, projects] = await Promise.all([
      db.moulds.toArray(),
      db.companies.toArray(),
      db.projects.toArray(),
    ]);
    const active = moulds.filter((m) => m.isActive !== false);
    const companyById = new Map(companies.map((c) => [c.id, c]));

    // Keyed by id, not by name: the dashboard row is a link now, and a link
    // needs something to filter by. Moulds with no owner share the null key.
    const ownerCounts = new Map<string | null, { name: string; count: number }>();
    const yearCounts = new Map<number, number>();
    for (const m of active) {
      const company = m.ownerCompanyId ? companyById.get(m.ownerCompanyId) : undefined;
      const key = company ? company.id : null;
      const name = company?.name ?? 'Unassigned';
      const seen = ownerCounts.get(key);
      ownerCounts.set(key, { name, count: (seen?.count ?? 0) + 1 });
      if (m.manufactureYear) yearCounts.set(m.manufactureYear, (yearCounts.get(m.manufactureYear) ?? 0) + 1);
    }

    return {
      total: active.length,
      withPhoto: active.filter((m) => !!m.photoUrl).length,
      pendingSync: active.filter((m) => m._pending === 1).length,
      projects: projects.length,
      companies: companies.length,
      byOwner: [...ownerCounts.entries()]
        .map(([id, { name, count }]) => ({ id, name, count }))
        .sort((a, b) => b.count - a.count),
      byYear: [...yearCounts.entries()]
        .map(([year, count]) => ({ year, count }))
        .sort((a, b) => a.year - b.year),
    } satisfies CatalogStats;
  }, []);
}

/** Unresolved sync conflicts, newest first. */
export function useConflicts() {
  return useLiveQuery(() => db.conflicts.where('resolved').equals(0).reverse().sortBy('detectedAt'));
}

/** Mutations the server refused, awaiting a human decision. */
export function useFailedMutations() {
  return useLiveQuery(() => db.outbox.where('status').equals('failed').toArray());
}
